import React from 'react';

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  if (endDeg - startDeg < 0.01) return '';
  const s = polarToCartesian(cx, cy, r, startDeg);
  const e = polarToCartesian(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${e.x.toFixed(
    3
  )} ${e.y.toFixed(3)}`;
}

/**
 * StackedRingsChart
 *
 * Renders concentric ring arcs — one ring per segment, outermost first.
 * Each arc starts at 12 o'clock and runs clockwise by (value / total) * 360°.
 *
 * Props:
 *   rings       — Array<{ value: number, total: number, color: string }>
 *   label       — string | number | null  (centred text; null → em-dash)
 *   sublabel    — string | null           (small muted text below label)
 *   size        — number  (svg width/height px, default 140)
 *   strokeWidth — number  (ring thickness px, default 8)
 *   gap         — number  (px gap between ring edges, default 4)
 *   trackColor  — string  (background ring colour)
 *   className   — string
 */
export const StackedRingsChart = ({
  rings = [],
  label = null,
  sublabel = null,
  size = 140,
  strokeWidth = 8,
  gap = 4,
  trackColor = 'var(--neutral-dark-6)',
  className = ''
}) => {
  const half = size / 2;
  const spacing = strokeWidth + gap;
  const max_r = half - strokeWidth / 2 - 2;

  const computed = rings.map((ring, i) => {
    const r = max_r - i * spacing;
    const frac = ring.total > 0 ? ring.value / ring.total : 0;
    // cap at 359.99° so a full ring stays as a path (not degenerate)
    const end_deg = Math.min(frac * 360, 359.99);
    return { r, frac, end_deg, color: ring.color };
  });

  // centre label fits inside the innermost ring's inner edge
  const label_y = sublabel ? half - strokeWidth * 0.6 : half;
  const sublabel_y = half + strokeWidth * 0.9;

  const label_font = Math.max(10, strokeWidth * 1.6);
  const sublabel_font = Math.max(8, strokeWidth * 1.1);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      {computed.map(({ r, frac, end_deg, color }, i) => (
        <React.Fragment key={i}>
          <circle
            cx={half}
            cy={half}
            r={r}
            fill="none"
            stroke={trackColor}
            strokeWidth={strokeWidth}
          />
          {frac > 0 && (
            <path
              d={arcPath(half, half, r, 0, end_deg)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          )}
        </React.Fragment>
      ))}

      {label !== null ? (
        <>
          <text
            x={half}
            y={label_y}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: label_font,
              fontWeight: 700,
              fill: 'var(--neutral-dark-1)',
              fontFamily: 'inherit',
              letterSpacing: '-0.5px'
            }}
          >
            {label}
          </text>
          {sublabel && (
            <text
              x={half}
              y={sublabel_y}
              textAnchor="middle"
              style={{
                fontSize: sublabel_font,
                fill: 'var(--neutral-dark-4)',
                fontFamily: 'inherit'
              }}
            >
              {sublabel}
            </text>
          )}
        </>
      ) : (
        <text
          x={half}
          y={half}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: label_font, fill: 'var(--neutral-dark-4)', fontFamily: 'inherit' }}
        >
          —
        </text>
      )}
    </svg>
  );
};
